import { createSelector } from 'reselect';
import { AssetTableColumn } from '../config/types';
import {
  getAssetManagerSelectedAssetTypeId,
  getAssetManagerShowPlanView,
  getAssetTypesById,
  getBuildingTemplate,
  getLayerTypesKeyedById,
} from '.';
import map from './map';
import { DEFAULT_COLUMNS, PLAN_COLUMNS } from '../config/constants';

export const getListViewByAssetManagerId = createSelector(
  [map.features],
  (features): Map<string, { [key: string]: Array<AssetTableColumn> }> => {
    type AssetManagerMap = Map<string, { [key: string]: Array<AssetTableColumn> }>;
    const listViewByAssetManagerId:AssetManagerMap = new Map();

    if (features) {
      const featuresKeys = Object.keys(features);
      featuresKeys?.forEach((key) => {
        listViewByAssetManagerId.set(key, features[key]?.list_view);
      });
    }
    return listViewByAssetManagerId;
  },
);

export const getListViewConfig = createSelector([
  map.assetManagerId,
  getListViewByAssetManagerId,
  getAssetManagerSelectedAssetTypeId,
], (
  assetManagerId,
  listViewByAssetManagerId,
  selectedAssetTypeId,
) => {
  const currentFeatureListViews = listViewByAssetManagerId.get(assetManagerId) || {};
  return currentFeatureListViews[selectedAssetTypeId];
});

export const getListViewDefaultSort = createSelector([
  getListViewConfig,
], (
  listViewConfig,
) => {
  const defaultSortColumn = listViewConfig?.find(col => col.default_sort);
  if (!defaultSortColumn) {
    return undefined;
  }
  const { key, default_sort: defaultSort } = defaultSortColumn;
  return {
    key,
    direction: defaultSort!,
  };
});

export const getListViewColumns = createSelector([
  map.assetManagerId,
  getListViewConfig,
  getAssetManagerSelectedAssetTypeId,
  getAssetTypesById,
  getAssetManagerShowPlanView,
  getBuildingTemplate,
  getLayerTypesKeyedById,
], (
  assetManagerId,
  listViewConfig,
  selectedAssetTypeId,
  assetTypesById,
  showPlanView,
  buildingTemplate,
  layerTypesKeyedById,
): Array<AssetTableColumn> => {
  if (!listViewConfig) {
    return DEFAULT_COLUMNS;
  }
  let listView = [...listViewConfig];

  if (assetManagerId === 'status_tracking') {
    listView[0] = { ...listView[0], fixed: 'left' };
  }

  const statusPosition = listView.findIndex(({ key }) => key === 'statusUpdatedDate') + 1;

  if (showPlanView && statusPosition) {
    listView = [
      ...listView.slice(0, statusPosition),
      ...PLAN_COLUMNS,
      ...listView.slice(statusPosition),
    ];
  }

  const fields = assetTypesById.get(selectedAssetTypeId)?.fields || [];
  const fieldPropertiesById = new Map(
    fields.map(field => [field.id, field.properties || {}]),
  );

  listView = listView.map(column => ({
    ...column,
    properties: fieldPropertiesById.get(column.key) || {},
  }));

  const listAllLayersIndex = listView.findIndex(({ custom }) => custom === 'listAllLayers');
  if (listAllLayersIndex > -1) {
    const beforeColumns = listView.slice(0, listAllLayersIndex);
    const afterColumns = listView.slice(listAllLayersIndex + 1);
    if (!buildingTemplate?.primary_layer_path) return listView;
    const layerColumns: AssetTableColumn[] = buildingTemplate.primary_layer_path
      .slice(1)
      .map((layerType) => {
        const layerTypeData = layerTypesKeyedById.get(layerType);
        // Having all layer columns share the same order is not an issue
        // as the array order will be maintained when sorted.
        return ({
          custom: 'matchLayerType',
          key: layerTypeData?.name ?? '',
          title: layerTypeData?.name ?? '',
          layerType,
          filter_enabled: false,
          sort_enabled: true,
        });
      });
    listView = [...beforeColumns, ...layerColumns, ...afterColumns];
  }

  return listView;
});

export const getColumnsKeyedById = createSelector([getListViewColumns], listViewColumns => new Map(
  listViewColumns.map(column => [column.key, column]),
) as Map<string, AssetTableColumn>);

export const getCustomColumns = createSelector(
  [getListViewColumns],
  listViewColumns => listViewColumns.filter(({ custom }) => custom),
);
